import React, { useState } from "react";
import { Box, ClickAwayListener, Tooltip } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "../../../hooks/useTranslation";

interface IProps {
  isTextOnly?: boolean;
}

/**
 * Small warning pill shown next to an address saved as TEXT ONLY (no coordinates — excluded
 * from the map and route optimization). Visual language mirrors the red "Manual" pill in
 * ContactInfoModal, with a warning icon + tooltip explaining the limitation. The tooltip works
 * on hover (desktop) AND tap (mobile, via a controlled `open` state toggled on click, since MUI
 * Tooltip's default hover-only behavior doesn't fire on touch devices).
 */
export const TextOnlyAddressBadge: React.FC<IProps> = ({ isTextOnly }) => {
  const { p } = useTranslation("contact_info");
  if (!isTextOnly) return null;
  return <AddressWarningPill label={p("text_only_badge")} tooltip={p("text_only_tooltip")} />;
};

/**
 * The orange warning pill itself, shared by every address-status badge ("Text only",
 * "Fix address") so they read as one family. Tooltip on hover (desktop) AND tap (mobile).
 */
export const AddressWarningPill: React.FC<{ label: string; tooltip: string }> = ({ label, tooltip }) => {
  const [open, setOpen] = useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip
        title={tooltip}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disableFocusListener
      >
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          sx={{
            ml: 1, px: 1, py: "1px", borderRadius: "8px",
            backgroundColor: "#FFF4E5", color: "#8A5A00",
            fontSize: "11px", fontWeight: 600, textTransform: "uppercase",
            letterSpacing: "0.04em", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: "3px",
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "13px" }} aria-hidden />
          {label}
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};
